import { useEffect, useState, useRef } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Upload, FileText, X, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const empty = {
  title: "",
  description: "",
  area: "legislativo",
  type: "pdf",
  file_url: "",
  is_public: true,
  requires_validation: false,
  course_id: null,
};

export default function AdminMateriais() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [courses, setCourses] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/materials").then(r => setItems(r.data || [])).catch(() => {});
  useEffect(() => {
    load();
    api.get("/courses").then(r => setCourses(r.data || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.course_id) payload.course_id = null;
      let saved;
      if (editId) {
        const { data } = await api.put(`/admin/materials/${editId}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/admin/materials", payload);
        saved = data;
        setEditId(saved.id);
      }
      setEditingDoc(saved);
      toast.success("Salvo! Agora você pode anexar o PDF.");
      load();
    } catch {
      toast.error("Erro ao salvar");
    }
  };

  const edit = (m) => {
    setEditId(m.id);
    setForm({
      title: m.title || "",
      description: m.description || "",
      area: m.area || "legislativo",
      type: m.type || "pdf",
      file_url: m.file_url || "",
      is_public: m.is_public ?? true,
      requires_validation: !!m.requires_validation,
      course_id: m.course_id || null,
    });
    setEditingDoc(m);
    setOpen(true);
  };

  const remove = async (id) => {
    if (window.confirm("Excluir material?")) {
      await api.delete(`/admin/materials/${id}`);
      load();
    }
  };

  const onClose = () => {
    setOpen(false);
    setEditId(null);
    setEditingDoc(null);
    setForm(empty);
  };

  const onUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Arquivo maior que 20 MB.");
      e.target.value = "";
      return;
    }
    if (file.type !== "application/pdf") {
      toast.error("Apenas arquivos PDF são aceitos.");
      e.target.value = "";
      return;
    }
    if (!editId) {
      toast.error("Salve o material primeiro, depois anexe o arquivo.");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/materials/${editId}/upload-file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditingDoc(data);
      toast.success(`PDF enviado (${data.file_size_kb || "?"} KB)`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro no upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onRemoveFile = async () => {
    if (!editId) return;
    if (!window.confirm("Remover o PDF anexado?")) return;
    try {
      const { data } = await api.delete(`/admin/materials/${editId}/file`);
      setEditingDoc(data);
      load();
      toast.success("Arquivo removido");
    } catch {
      toast.error("Erro ao remover");
    }
  };

  const downloadFile = async (id, fileName) => {
    try {
      const { data } = await api.get(`/materials/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "material.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error("Erro ao baixar arquivo.");
    }
  };

  return (
    <AdminLayout title="Materiais">
      <div className="flex justify-end mb-3">
        <Button onClick={() => { setEditId(null); setForm(empty); setEditingDoc(null); setOpen(true); }} data-testid="new-material-btn">
          <Plus className="w-4 h-4 mr-1" /> Novo material
        </Button>
      </div>
      <Card className="rounded-xl"><CardContent className="p-0 overflow-x-auto">
        <Table><TableHeader><TableRow>
          <TableHead>Título</TableHead>
          <TableHead>Tipo</TableHead>
          <TableHead>Área</TableHead>
          <TableHead>Curso</TableHead>
          <TableHead className="text-center">PDF</TableHead>
          <TableHead className="text-center">Acesso</TableHead>
          <TableHead className="text-center">Downloads</TableHead>
          <TableHead className="text-right">Ações</TableHead>
        </TableRow></TableHeader>
          <TableBody>
            {items.map(m => {
              const courseTitle = courses.find(c => c.id === m.course_id)?.title;
              return (
                <TableRow key={m.id} data-testid={`row-mat-${m.id}`}>
                  <TableCell><p className="font-medium">{m.title}</p><p className="text-xs text-muted-foreground line-clamp-1">{m.description}</p></TableCell>
                  <TableCell><Badge variant="secondary" className="capitalize">{m.type}</Badge></TableCell>
                  <TableCell className="capitalize">{m.area || "—"}</TableCell>
                  <TableCell className="text-xs">{courseTitle || "—"}</TableCell>
                  <TableCell className="text-center">
                    {m.has_file ? (
                      <Button variant="ghost" size="sm" onClick={() => downloadFile(m.id, m.file_name)} data-testid={`mat-download-${m.id}`}>
                        <FileText className="w-4 h-4 mr-1 text-emerald-600" />
                        <span className="text-xs text-emerald-700">Baixar</span>
                      </Button>
                    ) : (
                      <span className="text-[11px] text-muted-foreground italic">Sem PDF</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">{m.requires_validation ? <Badge className="bg-[hsl(var(--gold)/0.2)] text-[hsl(var(--gold-foreground))] border-[hsl(var(--gold)/0.4)]">Validados</Badge> : <Badge variant="outline">Público</Badge>}</TableCell>
                  <TableCell className="text-center">{m.download_count || 0}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => edit(m)} data-testid={`mat-edit-${m.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(m.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">Nenhum material cadastrado</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent className="max-w-[640px] rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? "Editar material" : "Novo material"}</DialogTitle>
            <DialogDescription>Apostilas, slides ou PDFs vinculados aos cursos.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Título *</Label>
              <Input required value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} data-testid="mat-title" />
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <Select value={form.type} onValueChange={(v) => setForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="mat-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apostila">Apostila</SelectItem>
                    <SelectItem value="slide">Apresentação</SelectItem>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Área</Label>
                <Select value={form.area || "legislativo"} onValueChange={(v) => setForm(f => ({ ...f, area: v }))}>
                  <SelectTrigger data-testid="mat-area"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="legislativo">Legislativo</SelectItem>
                    <SelectItem value="licitacoes">Licitações</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Curso vinculado (recomendado)</Label>
              <Select value={form.course_id || "none"} onValueChange={(v) => setForm(f => ({ ...f, course_id: v === "none" ? null : v }))}>
                <SelectTrigger data-testid="mat-course"><SelectValue placeholder="Sem vínculo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem vínculo (material geral)</SelectItem>
                  {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 p-3 bg-[hsl(var(--secondary))] rounded-lg">
              <Switch checked={form.requires_validation} onCheckedChange={(v) => setForm(f => ({ ...f, requires_validation: v }))} data-testid="mat-validation" />
              <div>
                <Label className="text-sm">Apenas alunos validados</Label>
                <p className="text-[11px] text-muted-foreground">Quando ligado, só alunos com conta validada podem baixar.</p>
              </div>
            </div>

            <Button type="submit" className="w-full" data-testid="mat-save">{editId ? "Salvar alterações" : "Cadastrar material"}</Button>
          </form>

          {/* Upload — só depois de salvar */}
          {editId && (
            <Card className="border-2 border-dashed mt-4" data-testid="mat-file-card">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">PDF do material</Label>
                  {editingDoc?.has_file && (
                    <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-700 bg-emerald-50">
                      <FileText className="w-3 h-3 mr-1" />
                      {editingDoc.file_name || "arquivo"} · {editingDoc.file_size_kb || "?"} KB
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  Anexe o <strong>PDF</strong> da apostila ou apresentação. Limite: 20 MB. O aluno poderá baixar direto no app.
                </p>
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  ref={fileRef}
                  className="hidden"
                  onChange={onUploadFile}
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    variant="outline"
                    className="flex-1"
                    data-testid="mat-upload-btn"
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    {uploading ? "Enviando…" : (editingDoc?.has_file ? "Substituir PDF" : "Enviar PDF")}
                  </Button>
                  {editingDoc?.has_file && (
                    <>
                      <Button type="button" variant="outline" size="icon" onClick={() => downloadFile(editId, editingDoc.file_name)} aria-label="Baixar atual">
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button type="button" variant="ghost" size="icon" onClick={onRemoveFile} className="text-rose-600" aria-label="Remover arquivo">
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          {!editId && (
            <p className="text-xs text-muted-foreground italic mt-2">
              💡 Para anexar o PDF, primeiro clique em "Cadastrar material" acima — em seguida o campo de upload aparecerá.
            </p>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
